import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { format as formatLocal } from 'date-fns';
import { Button, Card, DataTable, EmptyValue, Form, InputGroup, Modal, Spinner } from '@/components/ui';
import type { DataTableColumn } from '@/components/ui';
import { EquipmentRepairDetail, EquipmentRepairRow, Vehicle } from '../../../types/Vehicle';
import vehicleService from '../../../services/vehicleService';
import { formatDate } from '../../../utils/wrapDate';
import {
  formatHours,
  formatRate,
  odometerUnitOf,
  printRepairReport,
  repairStatusLabel
} from '../utils/equipmentPrint';
import EquipmentDateRangeModal, { REPORT_PRESET_MONTHS } from './EquipmentDateRangeModal';

interface EquipmentRepairHistoryTabProps {
  vehicleId: number;
  /** The unit, for the odometer unit and the print header; loaded by id when not passed. */
  vehicle?: Vehicle;
}

const SEARCH_DEBOUNCE_MS = 300;

function fmtOdometer(value: number | null | undefined, unit: string): React.ReactNode {
  return value == null ? <EmptyValue /> : `${value.toLocaleString('en-CA')} ${unit}`;
}

function taskLabel(row: EquipmentRepairRow): string {
  return [row.taskNumber, row.taskName].filter(Boolean).join(' · ');
}

function dailyLogHref(formId?: number | null): string | null {
  return formId ? `/submissions?formId=${formId}` : null;
}

/** Newest first by default; rows without a date sort last either way. */
function sortByDate(rows: EquipmentRepairRow[], dir: 'asc' | 'desc'): EquipmentRepairRow[] {
  const key = (row: EquipmentRepairRow) => formatDate(row.date) ?? '';
  return [...rows].sort((a, b) => {
    const ka = key(a);
    const kb = key(b);
    if (!ka || !kb) return ka ? -1 : kb ? 1 : 0;
    const cmp = ka.localeCompare(kb) || a.id - b.id;
    return dir === 'asc' ? cmp : -cmp;
  });
}

const EquipmentRepairHistoryTab: React.FC<EquipmentRepairHistoryTabProps> = ({ vehicleId, vehicle: vehicleProp }) => {
  const [rows, setRows] = useState<EquipmentRepairRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [selected, setSelected] = useState<EquipmentRepairRow | null>(null);
  const [detail, setDetail] = useState<EquipmentRepairDetail | null>(null);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [loadedVehicle, setLoadedVehicle] = useState<Vehicle | null>(null);
  const [showPrint, setShowPrint] = useState(false);

  const vehicle = vehicleProp ?? loadedVehicle;
  const unit = vehicle ? odometerUnitOf(vehicle) : 'km';

  useEffect(() => {
    if (vehicleProp) return;
    let cancelled = false;
    vehicleService.getVehicleById(vehicleId)
      .then((data) => {
        if (!cancelled) setLoadedVehicle(data);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [vehicleId, vehicleProp]);

  useEffect(() => {
    const handle = setTimeout(() => setSearch(searchInput.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [searchInput]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    vehicleService.getRepairHistory(vehicleId, search || undefined)
      .then((data) => {
        if (!cancelled) setRows(data);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load repair history');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [vehicleId, search]);

  useEffect(() => {
    if (!selected) return;
    let cancelled = false;
    setDetail(null);
    setDetailError(null);
    vehicleService.getRepairDetail(vehicleId, selected.id)
      .then((data) => {
        if (!cancelled) setDetail(data);
      })
      .catch((err) => {
        if (!cancelled) setDetailError(err instanceof Error ? err.message : 'Failed to load the repair');
      });
    return () => {
      cancelled = true;
    };
  }, [selected, vehicleId]);

  const sortedRows = useMemo(() => sortByDate(rows, sortDir), [rows, sortDir]);

  const columns: DataTableColumn<EquipmentRepairRow>[] = [
    {
      key: 'date',
      header: 'Date',
      sortable: true,
      mobile: 'title',
      className: 'tw-whitespace-nowrap',
      cell: (row) => (
        <button
          type="button"
          className="tw-border-0 tw-bg-transparent tw-p-0 tw-font-medium tw-text-primary hover:tw-underline"
          title="View Repair"
          onClick={(e) => {
            e.stopPropagation();
            setSelected(row);
          }}
        >
          {formatDate(row.date) ?? 'View Repair'}
        </button>
      )
    },
    {
      key: 'odometer',
      header: 'Odometer',
      align: 'right',
      className: 'tw-whitespace-nowrap',
      cell: (row) => fmtOdometer(row.odometer, unit)
    },
    { key: 'employee', header: 'Employee', cell: (row) => row.employeeName || <EmptyValue /> },
    { key: 'task', header: 'Task', mobile: 'subtitle', cell: (row) => taskLabel(row) || <EmptyValue /> },
    { key: 'status', header: 'Status', mobile: 'trailing', cell: (row) => repairStatusLabel(row.status) ?? <EmptyValue /> },
    {
      key: 'comments',
      header: 'Comments',
      className: 'tw-max-w-xs tw-truncate',
      cell: (row) => (row.comments ? <span title={row.comments}>{row.comments}</span> : <EmptyValue />)
    }
  ];

  const current: EquipmentRepairDetail | EquipmentRepairRow | null = detail ?? selected;
  const hours = current ? formatHours(current.hours) : null;
  const rate = current ? formatRate(current.rate, current.unitOfMeasure) : null;
  const logHref = dailyLogHref(current?.formId);

  const field = (label: string, value: React.ReactNode) => (
    <>
      <dt className="tw-text-content-muted">{label}</dt>
      <dd className="tw-mb-0">{value}</dd>
    </>
  );

  return (
    <>
      <Card>
        <Card.Header className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-2">
          <InputGroup className="md:tw-max-w-md">
            <InputGroup.Text>
              <i className="bi bi-search" aria-hidden="true" />
            </InputGroup.Text>
            <Form.Control
              type="search"
              placeholder="Search comments, task name or code..."
              aria-label="Search repair history"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
          </InputGroup>
          <Button size="sm" variant="outline-secondary" disabled={!vehicle} onClick={() => setShowPrint(true)}>
            <i className="bi bi-printer tw-mr-1" aria-hidden="true" />
            Print Repairs
          </Button>
        </Card.Header>
        <Card.Body className="tw-p-0">
          {loading ? (
            <div className="tw-py-6 tw-text-center">
              <Spinner role="status"><span className="tw-sr-only">Loading...</span></Spinner>
            </div>
          ) : error ? (
            <p className="tw-mb-0 tw-p-4 tw-text-sm tw-text-danger">{error}</p>
          ) : rows.length === 0 ? (
            <p className="tw-mb-0 tw-p-4 tw-text-sm tw-text-content-muted">
              {search
                ? 'No repair lines match your search.'
                : 'No mechanic time-card lines are stored against this unit.'}
            </p>
          ) : (
            <DataTable
              columns={columns}
              data={sortedRows}
              rowKey={(row) => row.id}
              onRowClick={(row) => setSelected(row)}
              sortBy="date"
              sortDir={sortDir}
              onSort={(_key, dir) => setSortDir(dir)}
            />
          )}
        </Card.Body>
      </Card>

      <Modal show={selected != null} onHide={() => setSelected(null)}>
        <Modal.Header closeButton>
          <Modal.Title>
            {selected?.date ? `View Repair · ${formatDate(selected.date) ?? ''}`.trim() : 'View Repair'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {detailError && <p className="tw-mb-2 tw-text-sm tw-text-danger">{detailError}</p>}
          {current && (
            <dl className="tw-mb-0 tw-grid tw-grid-cols-[8.5rem_minmax(0,1fr)] tw-gap-x-4 tw-gap-y-2 tw-text-sm">
              {field('Date', formatDate(current.date) ?? <EmptyValue />)}
              {field('Odometer', fmtOdometer(current.odometer, unit))}
              {field('Employee', current.employeeName || <EmptyValue />)}
              {field('Task', taskLabel(current) || <EmptyValue />)}
              {field('Status', repairStatusLabel(current.status) ?? <EmptyValue />)}
              {field('Hours', hours ?? <EmptyValue />)}
              {field('Rate', rate ?? <EmptyValue />)}
              {field(
                'Other Employees',
                !detail ? (
                  detailError ? <EmptyValue /> : <span className="tw-text-content-muted">Loading…</span>
                ) : detail.otherEmployees.length > 0 ? (
                  detail.otherEmployees.join(', ')
                ) : (
                  <EmptyValue />
                )
              )}
              {field(
                'Materials',
                !detail ? (
                  detailError ? <EmptyValue /> : <span className="tw-text-content-muted">Loading…</span>
                ) : detail.materials.length > 0 ? (
                  <ul className="tw-mb-0 tw-list-none tw-p-0">
                    {detail.materials.map((m, i) => (
                      <li key={`${m.name}-${i}`}>
                        {m.name}
                        {m.quantity != null && (
                          <span className="tw-text-content-secondary">
                            {' '}
                            ({m.quantity.toLocaleString('en-CA')}
                            {m.unit ? ` ${m.unit.toLowerCase()}` : ''})
                          </span>
                        )}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <EmptyValue />
                )
              )}
              {field('Comments', current.comments ? <span className="tw-whitespace-pre-wrap">{current.comments}</span> : <EmptyValue />)}
            </dl>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setSelected(null)}>Close</Button>
          {logHref && (
            <Link to={logHref} className="tw-inline-flex tw-items-center tw-rounded-md tw-bg-primary tw-px-3 tw-py-1.5 tw-text-sm tw-font-medium tw-text-white hover:tw-no-underline">
              <i className="bi bi-journal-text tw-mr-1" aria-hidden="true" />
              Open Daily Log
            </Link>
          )}
        </Modal.Footer>
      </Modal>

      {vehicle && (
        <EquipmentDateRangeModal
          show={showPrint}
          title="Print Repairs"
          description="Prints every repair line in the period, newest first, in one file."
          confirmLabel="Print"
          presetMonths={REPORT_PRESET_MONTHS}
          defaultPresetMonths={24}
          onHide={() => setShowPrint(false)}
          onConfirm={({ from, to }) => {
            const start = formatLocal(from, 'yyyy-MM-dd');
            const end = formatLocal(to, 'yyyy-MM-dd');
            return printRepairReport({
              vehicle,
              from,
              to,
              // The server returns every line in the range, not just the newest 200.
              load: async () =>
                sortByDate(await vehicleService.getRepairHistory(vehicleId, undefined, { from: start, to: end }), 'desc')
            });
          }}
        />
      )}
    </>
  );
};

export default EquipmentRepairHistoryTab;
